import { Link2, PhoneIncoming, PhoneMissed, PhoneOutgoing } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useSearchParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableCard, TableCardField, TableCardList } from '@/components/ui/table-card'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { formatDateTime, formatDuration } from '@/lib/formatters'
import {
  type TelephonyCallAttached,
  type TelephonyCallDirection,
  type TelephonyCallListItem,
  type TelephonyCallVendor,
  useTelephonyCalls,
} from '@/models/telephonyCalls'
import { TelephonySearchFilters } from '../Telephony/TelephonySearchFilters'
import { readTelephonySearch, telephonySearchUpdates } from '../Telephony/telephonySearchParams'
import { AttachToLeadDialog } from './AttachToLeadDialog'

function directionIcon(call: TelephonyCallListItem) {
  const missed = call.answeredAt === null
  if (missed) return <PhoneMissed className="size-4 text-orange-600" />
  return call.direction === 'inbound' ? (
    <PhoneIncoming className="size-4 text-green-600" />
  ) : (
    <PhoneOutgoing className="size-4 text-green-600" />
  )
}

function directionLabel(call: TelephonyCallListItem) {
  const missed = call.answeredAt === null
  if (missed) return call.direction === 'inbound' ? 'Missed · in' : 'Missed · out'
  return call.direction === 'inbound' ? 'Call · in' : 'Call · out'
}

function vendorBadge(vendor: TelephonyCallVendor) {
  if (vendor === 'dialpad') {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
        Dialpad
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">
      Aircall
    </Badge>
  )
}

function attachedLeads(call: TelephonyCallListItem) {
  if (call.attachedLeads.length === 0) {
    return <span className="text-muted-foreground text-sm italic">Unattached</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {call.attachedLeads.map((lead) => (
        <Link
          key={lead.id}
          to={`/insure/internal/sales-leads/${lead.id}`}
          className="text-sm text-blue-600 underline-offset-2 hover:underline"
        >
          {lead.name}
        </Link>
      ))}
    </div>
  )
}

interface AttachTarget {
  id: string
  vendor: TelephonyCallVendor
  hint: string
}

export function CallsIndex() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page') || '1')
  const direction = (searchParams.get('direction') || '') as TelephonyCallDirection | ''
  const attached = (searchParams.get('attached') || '') as TelephonyCallAttached | ''
  const missedOnly = searchParams.get('missed_only') === 'true'
  const search = readTelephonySearch(searchParams)

  const [attachTarget, setAttachTarget] = useState<AttachTarget | null>(null)

  function updateParams(updates: Record<string, string | undefined>) {
    const params = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v) params.set(k, v)
      else params.delete(k)
    }
    setSearchParams(params)
  }

  // `isLoading` is `isPending && isFetching`, so it goes false mid-retry.
  const { data, isPending, isError } = useTelephonyCalls({
    page,
    direction: direction || undefined,
    attached: attached || undefined,
    missedOnly,
    q: search.q || undefined,
    userId: search.userId || undefined,
    dateFrom: search.dateFrom || undefined,
    dateTo: search.dateTo || undefined,
  })
  const calls = data?.results ?? []

  function attachButton(call: TelephonyCallListItem) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          setAttachTarget({
            id: call.id,
            vendor: call.vendor,
            hint: `${call.fromNumber} → ${call.toNumber}`,
          })
        }
      >
        <Link2 className="size-4" />
        Attach
      </Button>
    )
  }
  const hasNext = !!data?.next
  const hasPrev = !!data?.previous

  return (
    <>
      <Helmet>
        <title>All Calls</title>
      </Helmet>
      <div className="flex flex-col gap-6 py-6 md:p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">All Calls</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <TelephonySearchFilters
              {...search}
              searchHint="Matches either number, the transcript and notes, and the lead or customer the call is on: name, DOT number, or a pasted lead or customer ID."
              onChange={(updates) => updateParams(telephonySearchUpdates(updates))}
            />
            <Select
              value={direction || 'all'}
              onValueChange={(v) => updateParams({ direction: v === 'all' ? undefined : v, page: undefined })}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any direction</SelectItem>
                <SelectItem value="inbound">Inbound only</SelectItem>
                <SelectItem value="outbound">Outbound only</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={attached || 'all'}
              onValueChange={(v) => updateParams({ attached: v === 'all' ? undefined : v, page: undefined })}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Attached" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All calls</SelectItem>
                <SelectItem value="false">Unattached only</SelectItem>
                <SelectItem value="true">Attached only</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={missedOnly ? 'default' : 'outline'}
              onClick={() => updateParams({ missed_only: missedOnly ? undefined : 'true', page: undefined })}
            >
              <PhoneMissed className="size-4" />
              {missedOnly ? 'Missed only ✓' : 'Missed only'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {/* Seven columns don't fit a phone, so under `lg` each call becomes a card. */}
            <div className="lg:hidden">
              {isPending && <SkeletonRows rows={6} />}
              {isError && (
                <div className="text-destructive py-6 text-center">
                  Couldn&rsquo;t load calls. Refresh to try again.
                </div>
              )}
              {!isPending && !isError && calls.length === 0 && (
                <div className="text-muted-foreground py-6 text-center">No calls match.</div>
              )}
              {!isPending && !isError && calls.length > 0 && (
                <TableCardList>
                  {calls.map((call) => (
                    <TableCard
                      key={`${call.vendor}-${call.id}`}
                      title={formatDateTime(call.startedAt)}
                      actions={attachButton(call)}
                    >
                      <TableCardField
                        label="Direction"
                        value={
                          <Badge variant="outline" className="gap-1">
                            {directionIcon(call)}
                            {directionLabel(call)}
                          </Badge>
                        }
                      />
                      <TableCardField label="Vendor" value={vendorBadge(call.vendor)} />
                      <TableCardField
                        label="From → To"
                        value={
                          <span className="font-mono text-xs">
                            {call.fromNumber} → {call.toNumber}
                          </span>
                        }
                      />
                      <TableCardField label="Duration" value={formatDuration(call.durationSeconds)} />
                      <TableCardField label="Attached leads" value={attachedLeads(call)} />
                    </TableCard>
                  ))}
                </TableCardList>
              )}
            </div>
            <Table className="max-lg:hidden">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>From → To</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Attached leads</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending && <TableSkeletonRows columns={7} />}
                {isError && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-destructive py-6 text-center">
                      Couldn&rsquo;t load calls. Refresh to try again.
                    </TableCell>
                  </TableRow>
                )}
                {!isPending && !isError && calls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground py-6 text-center">
                      No calls match.
                    </TableCell>
                  </TableRow>
                )}
                {calls.map((call) => {
                  const dur = formatDuration(call.durationSeconds)
                  return (
                    <TableRow key={`${call.vendor}-${call.id}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDateTime(call.startedAt)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          {directionIcon(call)}
                          {directionLabel(call)}
                        </Badge>
                      </TableCell>
                      <TableCell>{vendorBadge(call.vendor)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {call.fromNumber} → {call.toNumber}
                      </TableCell>
                      <TableCell className="text-sm">{dur ?? '—'}</TableCell>
                      <TableCell>{attachedLeads(call)}</TableCell>
                      <TableCell className="text-right">{attachButton(call)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {(hasPrev || hasNext) && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPrev}
              onClick={() => updateParams({ page: String(page - 1) })}
            >
              Previous
            </Button>
            <span className="text-muted-foreground text-sm">
              Page {page} · {data.count} total
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={() => updateParams({ page: String(page + 1) })}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <AttachToLeadDialog
        callId={attachTarget?.id ?? null}
        vendor={attachTarget?.vendor ?? null}
        hint={attachTarget?.hint ?? ''}
        onClose={() => setAttachTarget(null)}
      />
    </>
  )
}
